import React, { useCallback, useState, useEffect } from "react";
import {
    Modal,
    Box,
    Button,
    Checkbox,
    Typography,
    Avatar,
    Pagination,
    Accordion,
    AccordionSummary, AccordionDetails,
    Divider
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useService } from 'contexts/ServiceContextProvider';
import "../../css/main.css";
import "../../css/components/Modals/ListFilters.css";
import CheckIcon from "@mui/icons-material/Check";
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import { useSnackbar } from 'contexts/SnackbarContextProvider';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import ScrollAwareTooltip from "components/Custom/ScrollAwareTooltip";
export default function ListFiltersModal({
    openListFiltersModal,
    handleCloseFiltersModal,
    list,
    project,
    workspace_id,
    tempFilters,
    filters,
    labels,
    workers,
    currentSort
}) {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const [selectedFilters, setSelectedFilters] = useState({});
    const { getFilteredAndSortedList } = useService();
    const [labelPage, setLabelPage] = useState(1);
    const [userPage, setUserPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const itemsPerPage = 5;

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (tempFilters[list.id]) {
            const availableLabelsUUID = tempFilters[list.id].label?.split(',');
            const availableUsersUUID = tempFilters[list.id].user?.split(',');

            const availableLabels = labels
                .filter((item) =>
                    availableLabelsUUID?.some((uuid) => uuid === item.id)
                );

            const availableUsers = workers
                .filter((item) =>
                    availableUsersUUID?.some((uuid) => uuid === item.id)
                );

            setSelectedFilters((prev) => ({
                ...prev,
                [list.id]: {
                    labels: availableLabels,
                    users: availableUsers
                }
            }));
        }

    }, [openListFiltersModal]);


    const handleCheckboxChange = (item, type) => {
        setSelectedFilters((prev) => {
            const updatedFilters = { ...prev };

            if (!updatedFilters[list.id]) {
                updatedFilters[list.id] = { labels: [], users: [] };
            }

            if (type === "label") {
                if (updatedFilters[list.id].labels.some((label) => label.id === item.id)) {
                    updatedFilters[list.id].labels = updatedFilters[list.id].labels.filter((label) => label.id !== item.id);
                } else {
                    updatedFilters[list.id].labels.push(item);
                }
            } else if (type === "user") {
                if (updatedFilters[list.id].users.some((user) => user.id === item.id)) {
                    updatedFilters[list.id].users = updatedFilters[list.id].users.filter((user) => user.id !== item.id);
                } else {
                    updatedFilters[list.id].users.push(item);
                }
            }

            return updatedFilters;
        });
    };

    const handleSave = useCallback(async () => {
        try {
            const formattedFilters = {
                label: selectedFilters[list.id].labels.map((item) => item.id).join(','),
                user: selectedFilters[list.id].users.map((item) => item.id).join(','),
            };

            const combinedParams = {
                ...formattedFilters,
                sort: currentSort?.[list.id] || 'order',
            };

            setLoading(true);
            const response = await getFilteredAndSortedList(workspace_id, project, list.id, combinedParams);
            setLoading(false);

            handleCloseFiltersModal(response, formattedFilters, true);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 404) {
                showSnackbar(t("modal.no_tasks_filter_err"), 'error');
            } else {
                console.error("Error saving filters:", error.message);
            }
        }
    }, [selectedFilters, list, handleCloseFiltersModal, workspace_id, project, currentSort]);

    const resetFilters = () => {
        setSelectedFilters((prev) => ({
            ...prev,
            [list.id]: { labels: [], users: [] },
        }));
    };

    const availableLabelsUUID = filters.label;
    const availableUsersUUID = filters.members;

    const availableLabels = labels.filter((item) =>
        availableLabelsUUID?.some((label) => label.value === item.id)
    )
        .map((item) => ({
            ...item,
            count: availableLabelsUUID.find((label) => label.value === item.id)?.count || 0
        }));
    const availableUsers = workers.filter((item) =>
        availableUsersUUID?.some((user) => user.value === item.id)
    )
        .map((item) => ({
            ...item,
            count: availableUsersUUID.find((user) => user.value === item.id)?.count || 0
        }));


    const paginatedLabels = availableLabels.slice((labelPage - 1) * itemsPerPage, labelPage * itemsPerPage);
    const paginatedUsers = availableUsers.slice((userPage - 1) * itemsPerPage, userPage * itemsPerPage);

    return (
        <Modal
            open={openListFiltersModal}
            onClose={() => handleCloseFiltersModal()}
            aria-labelledby="list-filters-title"
            aria-describedby="list-filters-description"
        >
            <Box className="list-filters-modal-box">
                {/* Modal Title */}
                <div className="div-top-filters">
                    <Typography id="list-filters-title" variant="h6" className="list-filters-modal-title">
                        {t("modal.list_filters")}
                    </Typography>
                    {(selectedFilters[list.id]?.labels.length > 0 || selectedFilters[list.id]?.users.length > 0) && (
                        <Button className="list-filters-reset-button" onClick={resetFilters}>
                            <RestartAltIcon />

                            <span className="reset-button-span">
                                {t("modal.clear")}
                            </span>
                        </Button>
                    )}
                </div>

                <Divider sx={{ height: "2px", backgroundColor: "#444444", mb: 2 }}></Divider>


                {/* Modal Body */}
                <Box className="list-filters-modal-body">
                    {/* Label Filter */}
                    <Accordion sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'lightblue', ml: 1 }} />} className="accordion-summary">
                            <Typography variant="h6">
                                {t("modal.label")}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="custom-accordion-details">
                            {availableLabels.length === 0 ? (
                                <Typography className="list-filters-no-tasks">
                                    {t("modal.no_tasks_with_labels")}
                                </Typography>
                            ) : (
                                paginatedLabels.map((label) => (
                                    <div key={label.id} className="list-filters-workers-list">
                                        <div className="list-filters-label-div">
                                            <Checkbox
                                                checked={selectedFilters[list.id]?.labels?.some((item) => item.id === label.id) || false}
                                                onChange={() => handleCheckboxChange(label, "label")}
                                            />
                                            <div
                                                className={`list-filters-label-style ${label.color === "rainbow" ? "rainbow" : ""} 
                                                    ${selectedFilters[list.id]?.labels?.some((item) => item.id === label.id) ? "selected" : ""}`}
                                                style={{ backgroundColor: label.color }}
                                                onClick={() => handleCheckboxChange(label, "label")}
                                            >
                                                <Typography variant="body2" className="white-txt-color"
                                                    style={{ overflowWrap: 'anywhere' }}>
                                                    {label.title}
                                                </Typography>
                                                {selectedFilters[list.id]?.labels.some((item) => item.id === label.id) && (
                                                    <CheckIcon className="white-txt-color" />
                                                )}
                                            </div>
                                        </div>
                                        <ScrollAwareTooltip title={t('modal.filters_task_count', { count: label.count })} arrow>
                                            <span className="user-count">{label.count}</span>
                                        </ScrollAwareTooltip>
                                    </div>
                                ))
                            )}
                            {availableLabels.length >= 5 && (
                                <Pagination
                                    count={Math.ceil(availableLabels.length / itemsPerPage)}
                                    page={labelPage}
                                    onChange={(_, value) => setLabelPage(value)}
                                    className="list-filters-landing-page"
                                />
                            )}

                        </AccordionDetails>
                    </Accordion>


                    {/* User Filter */}
                    <Accordion sx={{ mb: 1 }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon sx={{ color: 'lightblue', ml: 1 }} />}
                            className="accordion-summary"
                        >
                            <Typography variant="h6">
                                {t("modal.user")}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="custom-accordion-details">
                            {availableUsers.length === 0 ? (
                                <Typography className="list-filters-no-tasks">
                                    {t("modal.no_tasks_with_users")}
                                </Typography>
                            ) : (
                                paginatedUsers.map((user) => (
                                    <div key={user.id} className="list-filters-workers-list">
                                        <div className="list-filters-label-div">
                                            <Checkbox
                                                checked={selectedFilters[list.id]?.users?.some((item) => item.id === user.id) || false}
                                                onChange={() => handleCheckboxChange(user, "user")}
                                            />
                                            <div
                                                onClick={() => handleCheckboxChange(user, "user")}
                                                className={`list-filters-member-list ${selectedFilters[list.id]?.users?.some((item) => item.id === user.id) ? "selected" : ""
                                                    }`}
                                            >
                                                <Avatar
                                                    alt={user.username}
                                                    src={user.image}
                                                    className="avatar-img-filter"
                                                />
                                                <Typography variant="body2" className="list-filters-worker-name">
                                                    {user.full_name}
                                                </Typography>
                                            </div>
                                        </div>
                                        <ScrollAwareTooltip title={t('modal.filters_task_count', { count: user.count })} arrow>
                                            <span className="user-count">{user.count}</span>
                                        </ScrollAwareTooltip>
                                    </div>
                                ))
                            )}
                            {availableUsers.length >= 5 && (
                                <Pagination
                                    count={Math.ceil(availableUsers.length / itemsPerPage)}
                                    page={userPage}
                                    onChange={(_, value) => setUserPage(value)}
                                    className="list-filters-landing-page"
                                />
                            )}
                        </AccordionDetails>
                    </Accordion>



                </Box>

                {/* Modal Actions */}
                <Box className="list-filters-modal-actions">
                    {/* Close Button */}
                    <Button className="decline-button list-filter-button" onClick={() => handleCloseFiltersModal()}>
                        <CancelIcon style={{ marginRight: 4 }} />

                        <span className="button-text">
                            {t("global.close")}
                        </span>
                    </Button>

                    {/* Search Buttons */}
                    <Button
                        onClick={handleSave}
                        className={`accept-button list-filter-button ${loading ? "loading" : ""}`}
                        disabled={loading}
                    >
                        <SearchIcon sx={{ m: 0 }} />
                        <span className="button-text">
                            {t("modal.search")}
                        </span>
                        {loading && <TinyCustomFallback />}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
